import * as React from 'react';

import {
  announcementWriteErrorMessage,
  type AnnouncementDto,
} from '@/api/announcements-admin-client';
import { useEditAnnouncementContent } from '@/api/use-announcements-admin';
import { RichHtmlEditor } from '@/components/rich-html-editor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { isModificationWindowOpen } from '@/lib/announcement-labels';
import {
  formatDateTime,
  formatRemaining,
} from '@/routes/admin/communication/announcements-format';

// =============================================================================
// announcement-modification-window.tsx — Bandeau « fenêtre de modification » + édition inline (R15-COM-03).
// =============================================================================
// Post-envoi immédiat : l'annonce est Modifiable et une fenêtre de modification est ouverte jusqu'à
// `modificationWindowExpiresAt`. Ce composant affiche un COMPTE À REBOURS et permet l'ÉDITION INLINE
// (titre + contenu) via PATCH /admin/announcements/:id/content TANT QUE la fenêtre est ouverte. Passé
// l'échéance (lazy read-side, ARCH GATE-7 A) : verrouillage, édition refusée (l'écran n'appelle plus).
//
// A11y (WCAG 2.1 AA) : l'échéance ABSOLUE (heure de fin) est le porteur accessible ; le compte à rebours qui
// s'égrène est un renfort VISUEL (aria-hidden) — jamais annoncé chaque seconde (anti-pattern de live-region).
// Formulaire au clavier, libellés associés, erreurs en role="alert".

export interface AnnouncementModificationWindowProps {
  readonly announcement: AnnouncementDto;
  /** Notifie la mise à jour du contenu (l'écran parent rafraîchit son annonce courante). */
  readonly onEdited: (updated: AnnouncementDto) => void;
  /** Horloge injectable (compte à rebours déterministe en test). Défaut : horloge système. */
  readonly nowFactory?: () => Date;
}

export function AnnouncementModificationWindow({
  announcement,
  onEdited,
  nowFactory = (): Date => new Date(),
}: AnnouncementModificationWindowProps): React.JSX.Element {
  const editContent = useEditAnnouncementContent();

  // Horloge de rendu : ré-évaluée chaque seconde pour égrener le compte à rebours et basculer au verrouillage.
  const [now, setNow] = React.useState<Date>(() => nowFactory());
  React.useEffect(() => {
    const id = setInterval(() => {
      setNow(nowFactory());
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, [nowFactory]);

  const open = isModificationWindowOpen(announcement, now);
  const expiresAt = announcement.modificationWindowExpiresAt;
  const remainingMs =
    expiresAt !== null ? new Date(expiresAt).getTime() - now.getTime() : 0;

  // Champs d'édition inline (le parent détient l'annonce ; état local d'édition, pas de localStorage).
  const [titre, setTitre] = React.useState(announcement.titre);
  const [contentRich, setContentRich] = React.useState(announcement.contentRich);

  // Resynchronise les champs si l'annonce éditée change d'identité (ouverture d'une autre annonce).
  React.useEffect(() => {
    setTitre(announcement.titre);
    setContentRich(announcement.contentRich);
  }, [announcement.announcementId, announcement.titre, announcement.contentRich]);

  const titreEmpty = titre.trim() === '';

  const onSave = React.useCallback((): void => {
    if (!open || titreEmpty) {
      return;
    }
    editContent.mutate(
      { announcementId: announcement.announcementId, patch: { titre, contentRich } },
      { onSuccess: (updated) => onEdited(updated) },
    );
  }, [
    open,
    titreEmpty,
    editContent,
    announcement.announcementId,
    titre,
    contentRich,
    onEdited,
  ]);

  return (
    <section
      aria-labelledby="modif-window-heading"
      className="space-y-4 rounded-lg border border-border bg-accent/30 p-4"
    >
      <div className="space-y-1">
        <h2 id="modif-window-heading" className="text-lg font-medium">
          Fenêtre de modification
        </h2>
        {open ? (
          <p className="text-sm text-foreground">
            L'annonce a été diffusée. Vous pouvez encore la modifier jusqu'à{' '}
            <strong>{formatDateTime(expiresAt)}</strong>.{' '}
            {/* Compte à rebours = renfort VISUEL uniquement (aria-hidden), l'heure ci-dessus est accessible. */}
            <span aria-hidden="true" className="tabular-nums text-muted-foreground">
              (temps restant : {formatRemaining(remainingMs)})
            </span>
          </p>
        ) : (
          <p role="status" className="text-sm text-muted-foreground">
            La fenêtre de modification est écoulée : l'annonce est verrouillée et ne
            peut plus être modifiée.
          </p>
        )}
      </div>

      {open ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="modif-titre">Titre de l'annonce</Label>
            <Input
              id="modif-titre"
              value={titre}
              onChange={(event) => setTitre(event.target.value)}
              aria-invalid={titreEmpty}
              aria-describedby={titreEmpty ? 'modif-titre-error' : undefined}
            />
            {titreEmpty ? (
              <p id="modif-titre-error" role="alert" className="text-sm text-destructive">
                Le titre est obligatoire.
              </p>
            ) : null}
          </div>

          <RichHtmlEditor
            id="modif-content"
            label="Contenu de l'annonce"
            hint="Modifiez le contenu diffusé. Il est nettoyé et sécurisé à l'enregistrement."
            value={contentRich}
            onChange={setContentRich}
          />

          {editContent.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {announcementWriteErrorMessage(
                editContent.error,
                'La modification a échoué.',
              )}
            </p>
          ) : null}

          <Button
            type="button"
            onClick={onSave}
            disabled={titreEmpty || editContent.isPending}
          >
            {editContent.isPending
              ? 'Enregistrement…'
              : 'Enregistrer les modifications'}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
